export const formatPhoneNumber = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "";
  }

  let digitsOnly = String(value).replace(/\D/g, "");

  // If it has 11 digits and starts with 1 (country code), remove the leading 1
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    digitsOnly = digitsOnly.slice(1);
  }

  // if (digitsOnly.length !== 10) {
  //   return String(value);
  // }

  const areaCode = digitsOnly.slice(0, 3);
  const centralOffice = digitsOnly.slice(3, 6);
  const stationCode = digitsOnly.slice(6, 10);

  return `${areaCode}-${centralOffice}-${stationCode}`;
};

export const enforcePhoneNumberFormatInInput = (value: string) => {
  const digitsOnly = value.replace(/\D/g, "").slice(0, 10);

  const areaCode = digitsOnly.slice(0, 3);
  const centralOffice = digitsOnly.slice(3, 6);
  const stationCode = digitsOnly.slice(6, 10);

  if (digitsOnly.length <= 3) {
    return areaCode;
  }

  if (digitsOnly.length <= 6) {
    return `${areaCode}-${centralOffice}`;
  }

  return `${areaCode}-${centralOffice}-${stationCode}`;
};

const COMPLETE_US_PHONE_PATTERN = /^\d{3}-\d{3}-\d{4}$/;

/** SERV-3074: true only for a finished XXX-XXX-XXXX number, the shape `enforcePhoneNumberFormatInInput` builds. */
export const isCompleteUsPhone = (value: string): boolean => COMPLETE_US_PHONE_PATTERN.test(value);

export const enforceSocialSecurityNumberFormat = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 9);

  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;

  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

/**
 * Converts a phone number to E.164 format (+1XXXXXXXXXX)
 * @param phoneNumber - Phone number in any format (e.g., "123-456-7890", "(123) 456-7890", "1234567890")
 * @returns Phone number in E.164 format or empty string if invalid
 */
export const convertToE164Format = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) {
    return "";
  }

  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  // If it starts with 1 and has 11 digits, remove the leading 1
  // If it has 10 digits, add +1 prefix
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return `+1${digitsOnly.slice(1)}`;
  } else if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }

  // If already in E.164 format, return as is
  if (phoneNumber.startsWith("+1") && digitsOnly.length === 11) {
    return phoneNumber;
  }

  // Return empty string if invalid
  return "";
};

/**
 * Converts E.164 format phone number (+1XXXXXXXXXX) to display format (XXX-XXX-XXXX)
 * @param phoneNumber - Phone number in E.164 format (e.g., "+15463464363")
 * @returns Phone number in display format (e.g., "546-346-4363") or empty string if invalid
 */
export const convertFromE164Format = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) {
    return "";
  }

  // Masked PII (e.g., "+1******3534") — return as-is, no digit parsing
  if (phoneNumber.includes("*")) {
    return phoneNumber;
  }

  // Remove all non-digit characters
  let digitsOnly = phoneNumber.replace(/\D/g, "");

  // If it starts with 1 and has 11 digits, remove the leading 1
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    digitsOnly = digitsOnly.slice(1);
  }

  // If it has 10 digits, format as XXX-XXX-XXXX
  if (digitsOnly.length === 10) {
    const areaCode = digitsOnly.slice(0, 3);
    const centralOffice = digitsOnly.slice(3, 6);
    const stationCode = digitsOnly.slice(6, 10);
    return `${areaCode}-${centralOffice}-${stationCode}`;
  }

  // Return empty string if invalid
  return "";
};

export const enforceE164FormatInInput = (value: string) => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, "").slice(0, 11);

  if (digitsOnly.length === 0) {
    return "";
  }

  if (digitsOnly.startsWith("1") && digitsOnly.length === 11) {
    return `+${digitsOnly}`;
  }

  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }

  if (digitsOnly.startsWith("1") && digitsOnly.length < 11) {
    return `+${digitsOnly}`;
  }

  if (digitsOnly.length < 10) {
    return `+1${digitsOnly}`;
  }

  return `+1${digitsOnly.slice(0, 10)}`;
};

export const convertToE164 = (phoneNumber: string): string => {
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return `+${digitsOnly}`;
  }

  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }

  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }

  if (phoneNumber.trim().startsWith("+1")) {
    const cleaned = phoneNumber.replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+${cleaned}`;
    }
  }

  console.error(`Invalid phone number format: ${phoneNumber}`);
  return "";
};

// Formatter functions for different input types - phone, ssn, ein
export const numberInputFormatters = {
  phone: (value: string) => {
    const d = value.replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  },

  ssn: (value: string) => {
    const d = value.replace(/\D/g, "").slice(0, 9);
    if (d.length <= 3) return d;
    if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  },

  ein: (value: string) => {
    const d = value.replace(/\D/g, "").slice(0, 9);
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}-${d.slice(2)}`;
  },
};
export type FormatType = "phone" | "ssn" | "ein";

export const MASK_BY_FORMAT_TYPE: Record<FormatType, string> = {
  phone: "000-000-0000",
  ssn: "000-00-0000",
  ein: "00-0000000",
};
